import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import {
  LogOut,
  User,
  Shield,
  MapPin,
  Phone,
  Mail,
  Music,
  DollarSign,
  Clock,
  History,
  FileText,
} from "lucide-react";
import { ThemeToggle } from "../components/ThemeToggle";
import { useTheme } from "../context/ThemeContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Theme-aware logos (SVG with transparent background)
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_glass-teach/artifacts/drr8t3sh_Dark_2.svg";
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_glass-teach/artifacts/6ix3baet_Light_2.svg";

export const InstructorProfile = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const logoUrl = theme === 'dark' ? LOGO_DARK : LOGO_LIGHT;
  const [profile, setProfile] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("mms_token")}` },
  });

  const fetchData = async () => {
    try {
      const [profileRes, historyRes] = await Promise.all([
        axios.get(`${API}/instructor/profile`, getAuthHeaders()),
        axios.get(`${API}/instructor/payment-history`, getAuthHeaders()),
      ]);
      setProfile(profileRes.data);
      setPaymentHistory(historyRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        localStorage.clear();
        navigate("/");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
    toast.success("Logged out successfully");
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-zinc-950' : 'bg-slate-50'}`}>
        <div className={`w-8 h-8 border-2 rounded-full animate-spin ${theme === 'dark' ? 'border-white/20 border-t-white' : 'border-indigo-200 border-t-indigo-600'}`} />
      </div>
    );
  }

  const totalEarned = paymentHistory.reduce((sum, p) => sum + p.amount, 0);
  const totalHoursPaid = paymentHistory.reduce((sum, p) => sum + p.hours_paid, 0);

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-zinc-950' : 'bg-slate-50'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-50 backdrop-blur-xl border-b ${theme === 'dark' ? 'bg-zinc-950/80 border-white/10' : 'bg-white/80 border-slate-200'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={logoUrl} alt="MMS Logo" className="h-12 w-auto object-contain logo-glass" />
            <span className={`text-lg font-semibold hidden sm:block ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>MMS Portal</span>
          </div>
          <nav className="flex items-center gap-2 sm:gap-4">
            <Link to="/dashboard" className="glass-button-ghost text-sm">
              Dashboard
            </Link>
            <Link to="/profile" className={`glass-button-ghost text-sm ${theme === 'dark' ? 'text-white' : 'text-indigo-600'}`}>
              Profile
            </Link>
            <Link to="/materials" className="glass-button-ghost text-sm">
              Materials
            </Link>
            <ThemeToggle />
            <button onClick={handleLogout} className="glass-button-ghost text-sm flex items-center gap-1">
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <h1 className={`text-3xl sm:text-4xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>Your Profile</h1>
          <p className={theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}>Legal information and financial summary</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Legal Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Personal Info Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="glass-card p-8"
            >
              <h2 className={`text-xl font-semibold mb-6 flex items-center gap-2 ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>
                <User className={`w-5 h-5 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`} />
                Personal Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className={`text-sm mb-1 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>Full Legal Name</p>
                  <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`} data-testid="profile-name">
                    {profile?.first_name && profile?.last_name 
                      ? `${profile.first_name} ${profile.last_name}`
                      : profile?.full_legal_name}
                  </p>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>Email</p>
                  <p className={`font-medium flex items-center gap-2 ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>
                    <Mail className={`w-4 h-4 ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`} />
                    {profile?.email}
                  </p>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>Date of Birth</p>
                  <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>
                    {profile?.date_of_birth 
                      ? new Date(profile.date_of_birth).toLocaleDateString()
                      : "-"}
                  </p>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>Contract Status</p>
                  <p className={`font-medium flex items-center gap-2 ${profile?.contract_uploaded ? 'text-green-400' : 'text-yellow-400'}`}>
                    {profile?.contract_uploaded ? "✓ Uploaded" : "⚠ Missing"}
                  </p>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>Tax ID / SSN</p>
                  <p className={`font-medium mono ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`} data-testid="profile-taxid">
                    {profile?.tax_id_ssn?.replace(/\d(?=\d{4})/g, "*")}
                  </p>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>Digital Signature</p>
                  <p className={`italic ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`} style={{ fontFamily: "'Dancing Script', cursive" }}>
                    {profile?.digital_signature}
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Address & Emergency Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-card p-8"
            >
              <h2 className={`text-xl font-semibold mb-6 flex items-center gap-2 ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>
                <Shield className={`w-5 h-5 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`} />
                Contact & Emergency
              </h2>

              <div className="space-y-6">
                <div>
                  <p className={`text-sm mb-1 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>Physical Address</p>
                  <p className={`font-medium flex items-start gap-2 ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>
                    <MapPin className={`w-4 h-4 mt-0.5 flex-shrink-0 ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`} />
                    {profile?.physical_address}
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className={`text-sm mb-1 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>Emergency Contact</p>
                    <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>{profile?.emergency_contact_name}</p>
                  </div>
                  <div>
                    <p className={`text-sm mb-1 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>Emergency Phone</p>
                    <p className={`font-medium flex items-center gap-2 ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>
                      <Phone className={`w-4 h-4 ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`} />
                      {profile?.emergency_contact_phone}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Authorized Courses */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="glass-card p-8"
            >
              <h2 className={`text-xl font-semibold mb-6 flex items-center gap-2 ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>
                <Music className={`w-5 h-5 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`} />
                Authorized Courses
              </h2>

              <div className="flex flex-wrap gap-3">
                {profile?.authorized_courses?.map((course) => (
                  <span
                    key={course}
                    data-testid={`authorized-course-${course.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`px-4 py-2 rounded-full text-sm border ${
                      theme === 'dark' 
                        ? 'bg-white/10 text-white border-white/10' 
                        : 'bg-indigo-50 text-indigo-700 border-indigo-100'
                    }`}
                  >
                    {course}
                  </span>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Right Column - Financial Summary */}
          <div className="space-y-6">
            {/* Current Balance */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-card p-8"
            >
              <h2 className={`text-xl font-semibold mb-6 flex items-center gap-2 ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>
                <DollarSign className={`w-5 h-5 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`} />
                Current Balance
              </h2>

              <div className="space-y-4">
                <div className={`p-4 rounded-xl border ${theme === 'dark' ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-100'}`}>
                  <p className={`text-sm mb-1 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>Amount Due</p>
                  <p className={`text-3xl font-bold mono ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`} data-testid="profile-amount-due">
                    ${profile?.amount_due?.toFixed(2)}
                  </p>
                </div>
                <div className={`p-4 rounded-xl border ${theme === 'dark' ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-100'}`}>
                  <p className={`text-sm mb-1 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>Unpaid Hours</p>
                  <p className={`text-2xl font-bold mono flex items-center gap-2 ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>
                    <Clock className={`w-5 h-5 ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`} />
                    {profile?.unpaid_hours?.toFixed(1)}h
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Lifetime Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="glass-card p-8"
            >
              <h2 className={`text-xl font-semibold mb-6 flex items-center gap-2 ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>
                <FileText className={`w-5 h-5 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`} />
                Lifetime Stats
              </h2>

              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className={theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}>Total Earned</span>
                  <span className={`font-semibold mono ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>${totalEarned.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className={theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}>Hours Paid</span>
                  <span className={`font-semibold mono ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>{totalHoursPaid.toFixed(1)}h</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className={theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}>Payments Received</span>
                  <span className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>{paymentHistory.length}</span>
                </div>
              </div>
            </motion.div>

            {/* Payment History */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="glass-card p-8"
            >
              <h2 className={`text-xl font-semibold mb-6 flex items-center gap-2 ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>
                <History className={`w-5 h-5 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`} />
                Payment History
              </h2>

              {paymentHistory.length === 0 ? (
                <p className={`text-center py-4 ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>No payments yet</p>
              ) : (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {paymentHistory.map((payment) => (
                    <div
                      key={payment.id}
                      data-testid={`payment-${payment.id}`}
                      className={`p-4 rounded-xl border ${theme === 'dark' ? 'bg-white/5 border-white/5' : 'bg-slate-50 border-slate-100'}`}
                    >
                      <div className="flex justify-between items-start mb-1">
                        <span className={`font-semibold mono ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>
                          ${payment.amount.toFixed(2)}
                        </span>
                        <span className="text-xs text-green-400 bg-green-500/20 px-2 py-0.5 rounded-full">
                          Paid
                        </span>
                      </div>
                      <p className={`text-xs ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>
                        {payment.hours_paid.toFixed(1)}h • {new Date(payment.processed_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  );
};
